// Kimenő üzenet SZERKESZTÉSE — jobb oldali FormSheet: az 5 tartalom-mező (tárgy/törzs/3 link) + a MELLÉKLETEK
// panel (a hordozó már létezik, tehát a feltöltés azonnali — a formtól függetlenül). A címzett
// utólag SOSEM szerkeszthető — legacy-parity). CSAK a saját üzenet (a sor-akciót a lista owner-gate-eli, a
// BFF a fal). A lista nem hordoz body-t → nyitáskor a detail-endpointról töltjük elő a tartalmat; a törzs
// egyszerű (p/br) HTML-je szöveggé bontva szerkesztődik, az összetett (legacy TipTap) HTML forrásként
// (jelezve — formázás-veszteség nélkül). SENT üzenetnél figyelmeztető sáv: a címzettek NEM kapnak értesítést,
// az üzeneten „szerkesztve" jelzés lesz (a revízió-snapshotot a BFF készíti a PATCH előtt). OCC: If-Match a
// detail friss verziójával (409 → conflict toast + lista-invalidálás).
import { type MessageRowSchema } from '@atlas/contracts';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { MessageAttachments } from '@/components/messages/message-attachments';
import { MessageContentFields } from '@/components/messages/message-content-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { messageKeys } from '@/features/messages/api';
import {
  buildMessageContentResolver,
  EMPTY_MESSAGE_CONTENT,
  toContentBody,
  type MessageContentValues,
} from '@/features/messages/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type MessageRow = z.infer<typeof MessageRowSchema>;

export interface MessageEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A szerkesztendő (SAJÁT) üzenet sora — a tartalom a detail-endpointról töltődik. */
  message: MessageRow | null;
}

export function MessageEditSheet({
  open,
  onOpenChange,
  message,
}: MessageEditSheetProps): JSX.Element {
  const { t } = useTranslation();
  const id = message?.id ?? '';

  // A tartalom (body/linkek) a detail-endpointról — a lista-sor nem hordozza; a verzió is innen a legfrissebb.
  const detailQ = api.messageDetail.useQuery(
    messageKeys.detail(id),
    { params: { id } },
    { queryKey: messageKeys.detail(id), enabled: open && id !== '' },
  );
  const detail = detailQ.data?.status === 200 ? detailQ.data.body : undefined;

  const form = useForm<MessageContentValues>({
    resolver: buildMessageContentResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_MESSAGE_CONTENT,
  });
  const { reset, handleSubmit, setError } = form;

  // Előtöltés a betöltött detailből (nyitásonként friss — a query kulcsa az id).
  useEffect(() => {
    if (open && detail) {
      reset({
        subject: detail.subject,
        // A TÁROLT HTML nyílik meg a szerkesztőben (nincs plain-text visszabontás — a body mindig HTML).
        body: detail.bodyHtml,
        googleDriveLink: detail.googleDriveLink ?? '',
        googleMeetLink: detail.googleMeetLink ?? '',
        zoomLink: detail.zoomLink ?? '',
      });
    }
    if (!open) reset(EMPTY_MESSAGE_CONTENT);
  }, [open, detail, reset]);

  const mutation = useEntityMutation<MessageContentValues>({
    mutationFn: (values) =>
      api.messageUpdate.mutation({
        params: { id },
        body: toContentBody(values),
        // A detail verziója a legfrissebb (a sor-verzió a lista betöltésekori); OCC a fal.
        headers: { 'if-match': String(detail?.version ?? message?.version ?? 0) },
      }),
    invalidateKeys: [messageKeys.lists(), messageKeys.detail(id)],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_MESSAGE_CONTENT) {
        setError(field as keyof MessageContentValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: t('toast:messages.updated'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  const isSent = (detail?.status ?? message?.status) === 'sent';

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('messages.edit.title')}
      description={message?.subject ?? undefined}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('messages.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending || !detail}>
            {mutation.isPending ? t('messages.edit.saving') : t('messages.edit.save')}
          </Button>
        </>
      }
    >
      {isSent && (
        // SENT-figyelmeztetés: néma szerkesztés (nincs értesítés) + „szerkesztve" jelzés a listában/detailben.
        <div className="border-warning/40 bg-warning-soft flex gap-2.5 rounded-lg border p-3">
          <ExclamationTriangleIcon className="text-warning mt-0.5 size-5 shrink-0" aria-hidden />
          <p className="text-foreground text-sm">{t('messages.edit.sentWarning')}</p>
        </div>
      )}
      {detailQ.isLoading ? (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : !detail ? (
        <p className="text-muted-foreground text-sm">{t('messages.edit.loadError')}</p>
      ) : (
        <>
          <Form {...form}>
            <MessageContentFields />
          </Form>
          {/* MELLÉKLETEK a szerkesztőben (user-lelet, 2026-08-25: „időzített és már kiküldött üzenet
              szerkesztésénél hiányzik a melléklet, nem lehet feltölteni").
              MIÉRT MŰKÖDIK ITT: a melléklet-API a HORDOZÓHOZ kötött (`/messages/:id/attachments`), és a
              szerkesztésnél az üzenet MÁR LÉTEZIK — tehát a feltöltés azonnal mehet (nem kell a létrehozó-form
              köztes „csatolandó fájlok" állapota, mint az írás-formon). A panel a FORMON KÍVÜL áll: a fájlok
              a saját, azonnali útjukon mennek, a mentés-gomb pedig csak a tartalom-mezőket küldi — így egy
              feltöltés-hiba nem buktatja a szöveg-mentést, és fordítva. */}
          <Divider spacing="none" className="my-4" />
          <div>
            <MessageAttachments
              messageId={id}
              // Ez a sheet CSAK a saját üzenetre nyílik (a lista owner-gate-eli a sor-akciót, a BFF a fal),
              // tehát itt a melléklet-kezelés engedett.
              canManage
              enabled={open && id !== ''}
              className="space-y-3"
            />
          </div>
        </>
      )}
    </FormSheet>
  );
}
